/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useTranslation } from 'react-i18next';
import RecommendedJobs from '../Components/Marketplace/RecommendedJobs';
import BidsInProgress from '../Components/Marketplace/BidsInProgress';
import AllJobs from '../Components/Marketplace/AllJobs';
import NotInterestedJobs from '../Components/Marketplace/NotInterestedJobs';
import SubmittedBids from '../Components/Marketplace/SubmittedBids';
import AwardedJobs from '../Components/Marketplace/AwardedJobs';
import { PROCESS_EVENTS } from '../../../Shared/Constants/App';

interface JobTab {
  id: number;
  label: string;
  route: string;
}

export interface IMarketplaceTabProps {
  /** Debounced free-text search term owned by MarketplacePage. */
  searchText?: string;
}

interface MarketplaceTab {
  label: string;
  route: string;
  component: React.FC<IMarketplaceTabProps>;
}

interface GraphTab {
  id: number;
  label: string;
  route: string;
}

export const getMarketplaceTabs = (): MarketplaceTab[] => {
  const { t } = useTranslation('vendor');

  return [
    {
      label: t('vendor:recommended'),
      route: 'recommended-jobs',
      component: RecommendedJobs,
    },
    {
      label: t('vendor:allJobs'),
      route: 'all-jobs',
      component: AllJobs,
    },
    {
      label: t('vendor:notInterestedJobs'),
      route: 'not-interested-jobs',
      component: NotInterestedJobs,
    },
    {
      label: t('vendor:awardedJobs'),
      route: 'awarded-jobs',
      component: AwardedJobs,
    },
    {
      label: t('vendor:bidsIProgress'),
      route: 'bids-in-progress',
      component: BidsInProgress,
    },
    {
      label: t('vendor:submittedBids'),
      route: 'submitted-bids',
      component: SubmittedBids,
    },
  ];
};

export const MARKETPLACE_ROUTE_TO_EVENT_KEY: Record<string, keyof typeof PROCESS_EVENTS> = {
  'recommended-jobs': 'RecommendedJobs',
  'all-jobs': 'AllJobs',
  'not-interested-jobs': 'NotInterestedJobs',
  'awarded-jobs': 'AwardedJobs',
  'bids-in-progress': 'BidsInProgress',
  'submitted-bids': 'SubmittedBids',
};

export const getJobsTab = (): JobTab[] => {
  const { t } = useTranslation('vendor');

  return [
    {
      id: 0,
      label: t('vendor:managedJobs'),
      route: 'self-performed-jobs',
    },
    {
      id: 1,
      label: t('vendor:marketplaceBids'),
      route: 'sub-contractor-jobs',
    },
    {
      id: 2,
      label: t('vendor:localSalesJobs'),
      route: 'local-sales-jobs',
    },
    {
      id: 3,
      label: t('vendor:ongoingJobs'),
      route: 'ongoing',
    },
  ];
};

export const JOBS_ROUTE_TO_EVENT_KEY: Record<string, keyof typeof PROCESS_EVENTS> = {
  'self-performed-jobs': 'SelfPerformedJobs',
  'sub-contractor-jobs': 'SubContractorBids',
  'local-sales-jobs': 'SubContractorBids',
  ongoing: 'AllVendorJobs',
};

export const getJobDetailsTab = (): JobTab[] => {
  const { t } = useTranslation('dashboard');

  return [
    {
      id: 0,
      label: t('dashboard:overview'),
      route: 'overview',
    },
    {
      id: 1,
      label: t('dashboard:workTasks'),
      route: 'worktasks',
    },
    {
      id: 2,
      label: t('dashboard:associateOnTheJob'),
      route: 'associateOnTheJob',
    },
    {
      id: 3,
      label: t('dashboard:actionItems'),
      route: 'actionItems',
    },
  ];
};

// export const getJobDetailsTab = ({
//   isJobManagement,
//   status,
//   role,
//   t,
// }: {
//   isJobManagement: boolean;
//   status: string;
//   role?: UserType;
//   t: (key: string) => string; // ✅ Accept translation function
// }): JobTab[] => {
//   return [
//     {
//       id: 0,
//       label: t('dashboard:overview'),
//       route: 'overview',
//       condition: true, // Always show
//     },
//     {
//       id: 1,
//       label: t('dashboard:workTasks'),
//       route: 'worktasks',
//       condition: status && status !== 'Created',
//     },
//     {
//       id: 2,
//       label: t('dashboard:associateOnTheJob'),
//       route: 'associateOnTheJob',
//       condition: status && status !== 'Created' && role === UserType.Vendor && !isJobManagement,
//     },
//     {
//       id: 3,
//       label: t('dashboard:actionItems'),
//       route: 'actionItems',
//       condition: status && status !== 'Created' && !isJobManagement,
//     },
//   ].filter((tab) => tab.condition);
// };

export const getBidsTab = (): JobTab[] => {
  const { t } = useTranslation('vendor');

  return [
    {
      id: 0,
      label: t('vendor:selfPerformedJobs'),
      route: 'job-details',
    },
    {
      id: 1,
      label: t('vendor:subContractorBids'),
      route: 'bids',
    },
  ];
};

export const facilityTypes = [
  {
    facilityType: 'acc',
    facilityName: 'Gym',
  },
  {
    facilityType: 'office',
    facilityName: 'Building',
  },
  {
    facilityType: 'hospital',
    facilityName: 'Hospitals',
  },
  {
    facilityType: 'bank',
    facilityName: 'Bank',
  },
  {
    facilityType: 'railRoad',
    facilityName: 'Rail Road',
  },
  {
    facilityType: 'warehouse',
    facilityName: 'Industrial/Warehouse',
  },
  {
    facilityType: 'financial',
    facilityName: 'Financial Services',
  },
];

export const getGraphsTab = (): GraphTab[] => {
  return [
    {
      id: 0,
      label: 'Quality And Inspection',
      route: 'quality-and-inspection',
    },
    {
      id: 1,
      label: 'Job Performance',
      route: 'job-performance',
    },
  ];
};

export const VMS_LOGIN = 'vmsLogin';
